/**
 * Theme Events — Single-source IIFE
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Single source of truth for theme custom event classes. Registers all classes
 * and the `NAMES` map onto `window.Theme.events.*`. Both IIFE callers (alpine
 * stores, defer scripts) and ES Module callers (web components) read from the
 * same window namespace — zero sync hazard.
 *
 * Mechanism ported from the framework's fw-events.js (issue #15). Event NAMES
 * are Feelrooty's frozen vocabulary — byte-identical to the strings the theme
 * dispatched before this file existed, so every Alpine `@store:*.window`
 * listener keeps working untouched. Do NOT adopt framework names.
 *
 * LOCAL DIVERGENCE from the framework file: constructors take `(detail = {},
 * options = {})` instead of positional args. Feelrooty's dispatch sites build
 * detail objects inline (and `Theme.event(name, detail)` routes generic
 * details through `classFor(name)`), so a uniform passthrough keeps every
 * existing `e.detail` shape byte-compatible.
 *
 * MENTAL MODEL
 * ─────────────────────────────────────────────────────────────────────────────
 * Events are observable boundary signals between subsystems:
 *
 *   alpine stores / components     → dispatch ThemeEvent subclasses
 *   Alpine listeners / surfaces    → `@<name>.window` or `events.observe(...)`
 *
 * Every `new ThemeEvent(...)` auto-logs `Logger.ns('events').debug(name, detail)`
 * at construction. That means:
 *
 *   - Every dispatch is observable in Playwright via `getLogs('events')`.
 *   - Tests can wait on the log buffer instead of attaching DOM listeners.
 *
 * The `store:*` names in `Events` exported by store-events.js (the ES-module
 * bus: `emit()/on()`) are a separate surface — the bus stays; only raw DOM
 * `CustomEvent` dispatches route through here.
 *
 * LOAD ORDER
 * ─────────────────────────────────────────────────────────────────────────────
 * Must run AFTER `fw-logger.js` and BEFORE `alpine-stores.js`. Loaded via
 * `<script defer>` in `layout/theme.liquid` (IIFE, NOT ES Module —
 * alpine-stores.js needs these classes synchronously during its own defer
 * execution).
 *
 * USAGE (dispatchers)
 * ─────────────────────────────────────────────────────────────────────────────
 *   const { CartUpdateEvent } = window.Theme.events;
 *   document.dispatchEvent(new CartUpdateEvent({ cart }));
 *
 * USAGE (listeners)
 * ─────────────────────────────────────────────────────────────────────────────
 *   const { NAMES } = window.Theme.events;
 *   document.addEventListener(NAMES.cartUpdate, (e) => { ... });
 *
 *   // OR via the observed variant (logs handler invocation too):
 *   const unsub = window.Theme.events.observe(NAMES.cartUpdate, (e) => { ... });
 *   // later: unsub();
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 */

(function () {
  'use strict';

  window.Theme = window.Theme || {};

  // Logger is loaded before this file. Fall back to a quiet shim if not — but
  // that's a wiring bug, not a runtime expectation.
  const Logger = window.Theme.Logger || {
    ns: () => ({
      debug: () => {},
      info: () => {},
      warn: console.warn.bind(console),
      error: console.error.bind(console),
    }),
  };
  const log = Logger.ns('events');

  // ─────────────────────────────────────────────────────────────────────────────
  // ThemeEvent base — auto-logs in constructor
  // ─────────────────────────────────────────────────────────────────────────────
  //
  // Extends CustomEvent so `e.detail` works the natural way (no manual
  // assignment). Auto-logs at the boundary between dispatcher and listener:
  // exactly once per event, capturing name + detail.
  // ─────────────────────────────────────────────────────────────────────────────

  class ThemeEvent extends CustomEvent {
    constructor(name, detail = {}, options = {}) {
      super(name, {
        bubbles: options.bubbles !== false,
        cancelable: !!options.cancelable,
        composed: !!options.composed,
        detail,
      });
      log.debug(name, detail);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Selection events (product page: flavor pills, quantity cards, plan bridge)
  // ─────────────────────────────────────────────────────────────────────────────

  class VariantChangedEvent extends ThemeEvent {
    static eventName = 'store:variant-changed';
    // detail: { productId, variant }
    constructor(detail = {}, options = {}) {
      super(VariantChangedEvent.eventName, detail, options);
    }
  }

  class CardSelectedEvent extends ThemeEvent {
    static eventName = 'store:card-selected';
    // detail: { productId, variantId, isBundle,
    //           packQty, totalDays, perServing, priceFormatted, compareFormatted, savePct }
    // Emitted by $store.bundle.sync() (assets/bundle-store.js).
    constructor(detail = {}, options = {}) {
      super(CardSelectedEvent.eventName, detail, options);
    }
  }

  class PriceRecalculatedEvent extends ThemeEvent {
    static eventName = 'store:price-recalculated';
    // detail: { productId, variantId, packQty, totalDays, perServing,
    //           priceFormatted, compareFormatted, savePct }
    constructor(detail = {}, options = {}) {
      super(PriceRecalculatedEvent.eventName, detail, options);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Cart events
  // ─────────────────────────────────────────────────────────────────────────────

  class CartUpdateEvent extends ThemeEvent {
    static eventName = 'store:cart:update';
    // detail: { cart, source? | sourceId?, data? } — `source: 'alpine-store'`
    // marks the store's own broadcast (its listener skips it to avoid loops)
    constructor(detail = {}, options = {}) {
      super(CartUpdateEvent.eventName, detail, options);
    }
  }

  class CartAddedEvent extends ThemeEvent {
    static eventName = 'store:cart:added';
    constructor(detail = {}, options = {}) {
      super(CartAddedEvent.eventName, detail, options);
    }
  }

  class CartErrorEvent extends ThemeEvent {
    static eventName = 'store:cart:error';
    // detail: { error, sourceId, data }
    constructor(detail = {}, options = {}) {
      super(CartErrorEvent.eventName, detail, options);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Discount events (cart drawer discount-code UI)
  // ─────────────────────────────────────────────────────────────────────────────

  class DiscountSuccessEvent extends ThemeEvent {
    static eventName = 'discount-success';
    constructor(detail = {}, options = {}) {
      super(DiscountSuccessEvent.eventName, detail, options);
    }
  }

  class DiscountErrorEvent extends ThemeEvent {
    static eventName = 'discount-error';
    // detail: { message }
    constructor(detail = {}, options = {}) {
      super(DiscountErrorEvent.eventName, detail, options);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Component lifecycle events
  // ─────────────────────────────────────────────────────────────────────────────

  class SliderChangeEvent extends ThemeEvent {
    static eventName = 'slider:change';
    // detail: { index, page, id } — dispatched on the slider element (bubbles)
    constructor(detail = {}, options = {}) {
      super(SliderChangeEvent.eventName, detail, options);
    }
  }

  class DeferredVideoLoadedEvent extends ThemeEvent {
    static eventName = 'deferred-video:loaded';
    // detail: { video } — dispatched on the <deferred-video> element (bubbles)
    constructor(detail = {}, options = {}) {
      super(DeferredVideoLoadedEvent.eventName, detail, options);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Event taxonomy — derived, single-sourced from the classes
  // ─────────────────────────────────────────────────────────────────────────────
  //
  // Each event name is declared exactly once: as the owning class's
  // `static eventName`. NAMES is built from this registry so the map and the
  // classes can never drift — a rename touches one place, and the registry
  // parity test (tests/e2e/smoke.spec.ts) pins every name.
  //
  // The registry keys are the public NAMES.* accessors (stable external
  // surface); the values are the owning classes.
  // ─────────────────────────────────────────────────────────────────────────────

  const EVENT_CLASSES = {
    // Selection domain (product page)
    variantChanged: VariantChangedEvent,
    cardSelected: CardSelectedEvent,
    priceRecalculated: PriceRecalculatedEvent,

    // Cart domain
    cartUpdate: CartUpdateEvent,
    cartAdded: CartAddedEvent,
    cartError: CartErrorEvent,

    // Discount domain
    discountSuccess: DiscountSuccessEvent,
    discountError: DiscountErrorEvent,

    // Component lifecycle
    sliderChange: SliderChangeEvent,
    deferredVideoLoaded: DeferredVideoLoadedEvent,
  };

  const NAMES = Object.freeze(
    Object.fromEntries(
      Object.entries(EVENT_CLASSES).map(([key, Cls]) => [key, Cls.eventName]),
    ),
  );

  // Name → class lookup, for generic dispatchers (`Theme.event(name, detail)`).
  const BY_NAME = Object.freeze(
    Object.fromEntries(
      Object.values(EVENT_CLASSES).map((Cls) => [Cls.eventName, Cls]),
    ),
  );

  function classFor(name) {
    return BY_NAME[name] || null;
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // observe(name, fn) — addEventListener with handler-side logging
  // ─────────────────────────────────────────────────────────────────────────────
  //
  // Use when you want the listen-side to show up in the log buffer too (handy
  // for debugging "did my handler fire?" without temporarily editing it).
  //
  // Hot paths can keep using native `document.addEventListener` directly —
  // every dispatch is already logged by the ThemeEvent constructor, so the
  // listen-side log is purely opt-in.
  //
  // Returns an unsubscribe function.
  // ─────────────────────────────────────────────────────────────────────────────

  function observe(name, fn, target = document) {
    const handler = (e) => {
      log.debug(`[heard] ${name}`, {
        detail: e.detail,
        handler: fn.name || 'anonymous',
      });
      return fn(e);
    };
    target.addEventListener(name, handler);
    return () => target.removeEventListener(name, handler);
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Expose
  // ─────────────────────────────────────────────────────────────────────────────

  // Frozen: the registry is the contract — no runtime additions or swaps.
  window.Theme.events = Object.freeze({
    NAMES,
    ThemeEvent,
    VariantChangedEvent,
    CardSelectedEvent,
    PriceRecalculatedEvent,
    CartUpdateEvent,
    CartAddedEvent,
    CartErrorEvent,
    DiscountSuccessEvent,
    DiscountErrorEvent,
    SliderChangeEvent,
    DeferredVideoLoadedEvent,
    classFor,
    observe,
  });

  log.info('Theme events loaded');
})();
